import React, { useState, useEffect, useRef, useCallback } from "react";
import "styles/AwardsFlowchart.css";

const steps = [
  {
    id: 1,
    title: "Start",
    description:
      "Follow the steps below to participate in the ESG Governance Awards. Begin your journey toward recognition for outstanding ESG governance practices.",
  },
  {
    id: 2,
    title: "Open Landing Page",
    description:
      "Visit the Landing Page — Access the official ESG Governance Awards website to learn more about the program, eligibility criteria, award categories, and important timelines.",
  },
  {
    id: 3,
    title: 'Click "Register Now"',
    description:
      'Click the "Register Now" button to begin your registration. This will direct you to the registration form where you can start the application process.',
  },
  {
    id: 4,
    title: "Fill Request Form",
    description:
      "Complete the Request Form — Fill in the short request form with your company details and contact information. Make sure all information is accurate and up-to-date.",
  },
  {
    id: 5,
    title: "Click Download Form",
    description:
      "Download the RFI Document — After submitting the form, you will be able to download the Request for Information (RFI) document. The RFI will also be sent to your registered email address.",
  },
  {
    id: 6,
    title: "RFI sent via email",
    description:
      "The RFI document will be automatically sent to your registered email address for your reference. Please check your inbox (and spam folder) to ensure you have received it.",
  },
  {
    id: 7,
    title: "Participant completes RFI offline",
    description:
      "Complete the RFI Offline — Complete the RFI document offline according to the provided guidelines. You may include additional ESG projects at this stage, if applicable.",
  },
  {
    id: 8,
    title: "Participant transfer the fee",
    description:
      "Make the Payment — Transfer the registration fee in accordance with the payment instructions provided. Please ensure the transfer is made from your company's official account and use the correct reference number.",
  },
  {
    id: 9,
    title: "Upload & submit RFI + transfer proof",
    description:
      "Submit RFI and Proof of Payment — Upload the completed RFI document and proof of payment through the submission page. Ensure both documents are complete before submitting.",
  },
  {
    id: 10,
    title: "Sent status verification via email: PENDING VERIFICATION",
    description:
      "Verification Process — Once submitted, your registration status will be marked as Pending Verification. You will receive an email confirming that your submission has been received and is under review.",
  },
  {
    id: 11,
    title: "Admin checks once a day",
    description:
      "Our team reviews submissions and payments on a daily basis. The admin will verify your payment against the bank records and validate your submitted documents.",
  },
  {
    id: 12,
    title: "Update Status: PAID",
    description:
      "If the payment is found and verified successfully, your registration status will be updated to PAID. This confirms that your financial obligation has been fulfilled.",
  },
  {
    id: 13,
    title: "Request clarification",
    description:
      "If the payment is not found or there are discrepancies, our team will reach out to request clarification. Please respond promptly to avoid delays in your registration process.",
  },
  {
    id: 14,
    title: "Email: Payment confirmed + receipt + next step",
    description:
      "Confirmation Email — After your payment is verified, you will receive a confirmation email including: payment confirmation, official receipt, and information on the next steps (assessment, workshop, and awarding process).",
  },
  {
    id: 15,
    title: "END",
    description:
      "Registration Complete — You have successfully completed the registration process. Our team will contact you regarding the next phases including assessment, workshop, and the awarding ceremony. Thank you for participating in the ESG Governance Awards!",
  },
];

const ArrowRight = () => (
  <div className="arr">
    <svg viewBox="0 0 60 40">
      <path
        d="M5 20L45 20L35 10M45 20L35 30"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const ArrowDown = ({ color }) => (
  <div className="m-arrow">
    <svg viewBox="0 0 24 32">
      <path
        d="M12 2 L12 24 L6 18 M12 24 L18 18"
        stroke={color || "currentColor"}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const AwardsFlowchart = () => {
  const [activeStep, setActiveStep] = useState(null);
  const chartRef = useRef(null);
  const svgRef = useRef(null);
  const uploadRef = useRef(null);
  const pendingRef = useRef(null);
  const adminRef = useRef(null);
  const clarifyRef = useRef(null);

  const openModal = (step) => setActiveStep(step);
  const closeModal = () => setActiveStep(null);

  const drawConnectors = useCallback(() => {
    const chart = chartRef.current;
    const svg = svgRef.current;
    const upload = uploadRef.current;
    const pending = pendingRef.current;
    const admin = adminRef.current;
    const clarify = clarifyRef.current;

    if (!chart || !svg || !upload || !pending || !admin || !clarify) return;
    if (window.innerWidth <= 900) {
      svg.innerHTML = "";
      return;
    }

    const aR = chart.getBoundingClientRect();
    const rel = (el) => {
      const r = el.getBoundingClientRect();
      return {
        t: r.top - aR.top,
        b: r.bottom - aR.top,
        cx: (r.left + r.right) / 2 - aR.left,
      };
    };

    const W = chart.scrollWidth,
      H = chart.scrollHeight;
    svg.setAttribute("width", W);
    svg.setAttribute("height", H);
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);

    const u = rel(upload),
      p = rel(pending),
      a = rel(admin),
      c = rel(clarify);
    const bendY = u.b + Math.round((p.t - u.b) / 2);
    const loopY = c.b + 28;

    svg.innerHTML = `
      <path d="M ${u.cx} ${u.b} L ${u.cx} ${bendY} L ${p.cx} ${bendY} L ${p.cx} ${p.t}" stroke="#003b6d" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M ${p.cx - 7} ${p.t - 9} L ${p.cx} ${p.t} L ${p.cx + 7} ${p.t - 9}" stroke="#003b6d" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M ${c.cx} ${c.b} L ${c.cx} ${loopY} L ${a.cx} ${loopY} L ${a.cx} ${a.b + 8}" stroke="#c0392b" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="6 3"/>
      <path d="M ${a.cx - 6} ${a.b + 14} L ${a.cx} ${a.b + 6} L ${a.cx + 6} ${a.b + 14}" stroke="#c0392b" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="6 3"/>
    `;
  }, []);

  useEffect(() => {
    const t1 = setTimeout(drawConnectors, 150);
    const t2 = setTimeout(drawConnectors, 500);
    window.addEventListener("resize", drawConnectors);
    let ro;
    if (typeof ResizeObserver !== "undefined" && chartRef.current) {
      ro = new ResizeObserver(drawConnectors);
      ro.observe(chartRef.current);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", drawConnectors);
      if (ro) ro.disconnect();
    };
  }, [drawConnectors]);

  return (
    <div className="awards-container">
      <div className="header">
        <div className="header-icon">
          <span></span>
        </div>
        <h2>Step by Step Registration Process</h2>
      </div>

      {/* ═══ DESKTOP ═══ */}
      <div className="desktop-flow">
        <div className="chart-area" ref={chartRef}>
          <svg id="connectorLayer" ref={svgRef}></svg>

          <div className="row row-1">
            <div className="step pill" onClick={() => openModal(steps[0])}>
              <h3>Start</h3>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[1])}>
              <h3>Open Landing Page</h3>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[2])}>
              <h3>Click Register Now</h3>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[3])}>
              <h3>Fill Request Form</h3>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[4])}>
              <h3>Click Download Form</h3>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[5])}>
              <h3>RFI sent via email</h3>
            </div>
            <ArrowRight />
            <div className="badge-wrap">
              <div className="badge-tag" onClick={() => openModal(steps[6])}>
                <span>Additional Project</span>
              </div>
              <ArrowDown />
              <div className="step" onClick={() => openModal(steps[6])}>
                <h3>Participant completes RFI offline</h3>
              </div>
            </div>
            <ArrowRight />
            <div className="step" onClick={() => openModal(steps[7])}>
              <h3>Participant transfer the fee</h3>
            </div>
            <ArrowRight />
            <div
              className="step"
              ref={uploadRef}
              onClick={() => openModal(steps[8])}
            >
              <h3>Upload &amp; submit RFI + transfer proof</h3>
            </div>
          </div>

          <div className="row row-2">
            <div
              className="step wide"
              ref={pendingRef}
              onClick={() => openModal(steps[9])}
            >
              <h3>Sent status verification via email: PENDING VERIFICATION</h3>
            </div>
            <ArrowRight />
            <div
              className="step"
              ref={adminRef}
              onClick={() => openModal(steps[10])}
            >
              <h3>Admin checks once a day</h3>
            </div>
            <div className="branch">
              <div className="branch-arm">
                <div className="branch-lbl-wrap">
                  <span className="branch-lbl ok">Payment found</span>
                  <ArrowRight />
                </div>
                <div className="step" onClick={() => openModal(steps[11])}>
                  <h3>Update Status: PAID</h3>
                </div>
              </div>
              <div className="branch-arm">
                <div className="branch-lbl-wrap">
                  <span className="branch-lbl nf">Payment not found</span>
                  <ArrowRight />
                </div>
                <div
                  className="step"
                  ref={clarifyRef}
                  onClick={() => openModal(steps[12])}
                >
                  <h3>Request clarification</h3>
                </div>
              </div>
            </div>
            <ArrowRight />
            <div className="step wide" onClick={() => openModal(steps[13])}>
              <h3>Email: Payment confirmed + receipt + next step</h3>
            </div>
            <ArrowRight />
            <div className="step pill" onClick={() => openModal(steps[14])}>
              <h3>END</h3>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ MOBILE ═══ */}
      <div className="mobile-flow">
        <div className="m-step m-pill" onClick={() => openModal(steps[0])}>
          <h3>Start</h3>
        </div>
        <ArrowDown />

        {[1, 2, 3, 4, 5].map((i) => (
          <React.Fragment key={i}>
            <div className="m-step" onClick={() => openModal(steps[i])}>
              <span className="m-step-num">Step {i + 1}</span>
              <h3>{steps[i].title}</h3>
            </div>
            <ArrowDown />
          </React.Fragment>
        ))}

        <div className="m-badge">
          <span>+ Additional Project</span>
        </div>
        <ArrowDown />
        <div className="m-step" onClick={() => openModal(steps[6])}>
          <span className="m-step-num">Step 7</span>
          <h3>Participant completes RFI offline</h3>
        </div>
        <ArrowDown />

        {[7, 8, 9, 10].map((i) => (
          <React.Fragment key={i}>
            <div className="m-step" onClick={() => openModal(steps[i])}>
              <span className="m-step-num">Step {i + 1}</span>
              <h3>{steps[i].title}</h3>
            </div>
            <ArrowDown />
          </React.Fragment>
        ))}

        <div className="m-branch-group">
          <div className="m-branch-side">
            <div className="m-branch-label ok">✓ Payment found</div>
            <ArrowDown />
            <div className="m-step" onClick={() => openModal(steps[11])}>
              <h3>Update Status: PAID</h3>
            </div>
          </div>
          <div className="m-branch-side">
            <div className="m-branch-label nf">✗ Payment not found</div>
            <ArrowDown color="#c0392b" />
            <div className="m-step" onClick={() => openModal(steps[12])}>
              <h3>Request clarification</h3>
              <p
                style={{
                  fontSize: "9px",
                  marginTop: "4px",
                  color: "rgba(255,255,255,.6)",
                }}
              >
                ↻ loops back to Admin
              </p>
            </div>
          </div>
        </div>
        <ArrowDown />

        <div className="m-step" onClick={() => openModal(steps[13])}>
          <span className="m-step-num">Step 14</span>
          <h3>Email: Payment confirmed + receipt + next step</h3>
        </div>
        <ArrowDown />

        <div className="m-step m-pill" onClick={() => openModal(steps[14])}>
          <h3>END</h3>
        </div>
      </div>

      <div className="footer">
        <a
          target="_blank"
          href="https://sustainability.co.id"
          rel="noopener noreferrer"
        >
          <p className="logo-text">www.sustainability.co.id</p>
        </a>
      </div>

      {/* Modal */}
      {activeStep && (
        <div className="overlay open" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-x" onClick={closeModal}>
              &times;
            </button>
            <div className="modal-hd">
              <div className="modal-num">Step {activeStep.id}</div>
              <h3>{activeStep.title}</h3>
            </div>
            <div className="modal-bd">
              <p>{activeStep.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AwardsFlowchart;
